import { useLayoutEffect, useRef } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { Footer, LangToggle, Nav } from './ui/chrome';
import { AccountPage } from './pages/Account';
import { HandoffPage } from './pages/Handoff';
import { LandingPage } from './pages/Landing';
import { FeaturePage } from './pages/Feature';
import { ResidentPage } from './pages/Resident';
import { LoginPage } from './pages/Login';
import { LegalPage } from './pages/Legal';
import { PreregPage } from './pages/Prereg';
import { SharePage } from './pages/Share';
import { ShopPage } from './pages/Shop';
import { SuccessPage } from './pages/Success';
import { useI18n } from './i18n';
import { useSession, useShopEntry } from './session';

/** Pages drawn like their concepts carry their own big wordmark instead of the nav bar. */
const OWN_HEADER = ['/login', '/account', '/shop'];

export function App() {
  const { pathname } = useLocation();
  // Before the new page paints, so it never shows at the old page's scroll position first.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  // Another language lays the page out again (English runs longer): fade it in rather than let it jump.
  const { lang } = useI18n();
  const main = useRef<HTMLElement>(null);
  const shownLang = useRef(lang);
  useLayoutEffect(() => {
    if (shownLang.current === lang) return;
    shownLang.current = lang;
    const el = main.current;
    if (!el) return;
    el.classList.remove('page-in');
    void el.offsetWidth;
    el.classList.add('page-in');
  }, [lang]);
  const own = OWN_HEADER.includes(pathname);
  // A shared chapter is read full screen, with the reader's own bars.
  // /s/ is the link people post (the API answers it with a preview, then sends them to /read/).
  if (pathname.startsWith('/read/') || pathname.startsWith('/s/')) {
    return (
      <Routes>
        <Route path="/read/:token" element={<SharePage />} />
        <Route path="/s/:token" element={<SharePage />} />
      </Routes>
    );
  }
  return (
    <>
      {own ? <Corner /> : <Nav />}
      {/* Each page fades in, so a change of layout between pages (the nav, the corner bar) reads as one move. */}
      <main key={pathname} ref={main} className="page-in">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features/:key" element={<FeaturePage />} />
          <Route path="/neighbours/:id" element={<ResidentPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/handoff" element={<HandoffPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route
            path="/account"
            element={
              <SignedIn>
                <AccountPage />
              </SignedIn>
            }
          />
          <Route path="/purchase/success" element={<SuccessPage />} />
          <Route path="/prereg" element={<PreregPage />} />
          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route path="/terms" element={<LegalPage page="terms" />} />
          <Route path="/support" element={<LegalPage page="support" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {/* With the page: a short page's footer would otherwise slide off as the next, longer one lays out. */}
      <Footer key={`footer:${pathname}`} />
    </>
  );
}

/** The small corner bar on pages without the nav: the other pages, and the language. */
function Corner() {
  const { t } = useI18n();
  const session = useSession();
  const shopEntry = useShopEntry();
  const { pathname } = useLocation();
  return (
    <div className={`corner on-${pathname.slice(1) || 'home'}`}>
      <Link to="/">{t('nav.home')}</Link>
      {shopEntry && pathname !== '/shop' ? <Link to="/shop">{t('nav.shop')}</Link> : null}
      {session && pathname !== '/account' ? <Link to="/account">{t('nav.account')}</Link> : null}
      <LangToggle />
    </div>
  );
}

/** Pages that need an account send visitors to sign in first, then back. */
function SignedIn({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const { pathname } = useLocation();
  return session ? children : <Navigate to={`/login?next=${encodeURIComponent(pathname)}`} replace />;
}
